import React, { useCallback, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { onMessage, Messaging } from "firebase/messaging";
import {
  requestPermission,
  getMessagingInstance,
  initializeAuthStateListener,
  ensureNotificationSession,
} from "./firebase/firebase";

import toast, { Toaster } from "react-hot-toast";
import { useAuth } from "./features/auth/authHooks";
import { AuthRoutes } from "./routes/AuthRoutes";
import { IndexRoutes } from "./routes/IndexRoutes";
// import FCMDebugger from "./components/FCMDebugger";
import "katex/dist/katex.min.css";


// Import FCM test utilities for development
import "./utils/testFCMFlow";

const App = (): React.ReactElement => {
  const { auth } = useAuth();

  const showForegroundNotification = useCallback((payload: unknown): void => {
    if (!payload) return;

    const p = payload as {
      notification?: { title?: string; body?: string };
      data?: {
        title?: string;
        body?: string;
        message?: string;
        thread_id?: string;
        ttl?: string | number;
        type?: string;
        rows_w_missing_fields_json?: string;
      };
    };
    const notificationTitle =
      p.notification?.title ||
      p.data?.title ||
      "Notification";
    const notificationBody =
      p.notification?.body ||
      p.data?.body ||
      p.data?.message ||
      "";
    const thread_id = p.data?.thread_id;
    const ttl = Number(p.data?.ttl || 60);

    const toastId = toast.success(
      <div 
        onClick={() => {
          if (!thread_id) {
            console.warn('Cannot navigate: thread_id is missing');
            return;
          }
          
          window.postMessage({ 
            type: 'NAVIGATE_TO_CHAT', 
            thread_id: thread_id,
            data: p.data || {},
          }, window.location.origin);
          
        }}
        style={{
          cursor: thread_id ? 'pointer' : 'default',
          padding: '12px',
          borderRadius: '8px',
          backgroundColor: '#10B981',
          color: 'white',
          fontWeight: 'bold',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
            {notificationTitle}
          </div>
          <button
            type="button"
            aria-label="Dismiss notification"
            onClick={(e) => {
              e.stopPropagation();
              toast.dismiss(toastId);
            }}
            style={{
              border: 'none',
              background: 'transparent',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              lineHeight: 1,
              padding: '0 2px',
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ fontSize: '14px', opacity: 0.9 }}>
          {notificationBody}
        </div>
        {thread_id && (
          <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}>
            Click to open chat • ID: {thread_id.substring(0, 8)}...
          </div>
        )}
      </div>,
      {
        duration: ttl * 1000,
        style: {
          cursor: thread_id ? 'pointer' : 'default',
          padding: '0',
          borderRadius: '8px',
          background: 'none',
          boxShadow: 'none'
        }
      }
    );
  }, []);

  useEffect(() => {
    const userEmail = auth?.user?.email;
    if (!userEmail) return;

    const maybeEnsureSession = async (payload: unknown): Promise<void> => {
      const p = payload as { data?: Record<string, unknown> };
      const threadId = typeof p?.data?.thread_id === "string" ? p.data.thread_id : null;
      if (!threadId) return;
      await ensureNotificationSession({
        userEmail,
        threadId,
        notificationData: p.data || {},
      });
    };

    const handleMessage = (event: MessageEvent): void => {
      if (event.data?.type === "SESSION_UPDATE" && event.data?.payload) {
        void maybeEnsureSession(event.data.payload);
      }
      if (event.data?.type === "NAVIGATE_TO_CHAT" && event.data?.thread_id) {
        void ensureNotificationSession({
          userEmail,
          threadId: String(event.data.thread_id),
          notificationData:
            event.data?.data && typeof event.data.data === "object"
              ? (event.data.data as Record<string, unknown>)
              : {},
        });
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [auth?.user?.email]);

  useEffect(() => {
    let unsubscribeMessaging: (() => void) | null = null;
    let isActive = true;

    const setupMessaging = async (): Promise<void> => {
      try {
        // Initialize auth state listener
        initializeAuthStateListener();

        let swRegistration: ServiceWorkerRegistration | null = null;
        if ('serviceWorker' in navigator) {
          swRegistration = await navigator.serviceWorker.getRegistration();

          // Ensure the Firebase messaging service worker is registered
          if (!swRegistration) {
            try {
              swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
            } catch (err) {
              console.warn('[App] Failed to register firebase-messaging-sw.js', err);
            }
          }
        }

        const messagingInstance = await getMessagingInstance();
        if (!messagingInstance) {
          return;
        }

        await requestPermission(swRegistration);

        if (!isActive) return;

        unsubscribeMessaging = onMessage(messagingInstance as Messaging, (payload) => {
          showForegroundNotification(payload);
          // Notify app to refresh sessions on foreground message
          try {
            window.postMessage({ type: 'SESSION_UPDATE', payload }, window.location.origin);
          } catch (e) {
            // no-op
          }
        });
      } catch (error) {
        console.error("Error initializing Firebase messaging:", error);
      }
    };

    setupMessaging();

    return () => {
      isActive = false;
      if (unsubscribeMessaging) {
        unsubscribeMessaging();
      }
    };
  }, [showForegroundNotification]);

  // Listen for navigation commands from the service worker and forward them
  useEffect(() => {
    const handleServiceWorkerMessage = (event: MessageEvent): void => {
      // Show a toast if the service worker forwards a session update (background push)
      if (event.data?.type === "SESSION_UPDATE" && event.data?.payload) {
        showForegroundNotification(event.data.payload);
      }

      if (event.data?.type !== "NAVIGATE_TO_CHAT") return;
      // Prevent re-forwarding the same message (avoid infinite loops)
      if (event.data?.__forwarded) return;

      const forwardedMessage = { ...event.data, __forwarded: true };
      // Forward the message to all windows (Main.tsx will handle it)
      window.postMessage(forwardedMessage, window.location.origin);
    };

    // Listen for messages from service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener("message", handleServiceWorkerMessage);
    }

    // Cleanup
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener("message", handleServiceWorkerMessage);
      }
    };
  }, [showForegroundNotification]);

  console.log('[App] Current Redux auth state:', {
    isUserLogin: auth?.isUserLogin,
    hasUser: !!auth?.user,
    userEmail: auth?.user?.email,
    sessionStorageUser: sessionStorage.getItem("user") ? "exists" : "missing"
  });

  const renderContent = (): React.ReactNode => {
    // Use Redux auth state for routing decisions
    if (auth?.isUserLogin) {
      console.log('[App] Rendering IndexRoutes (user is logged in via Redux)');
      return <IndexRoutes />;
    } else {
      console.log('[App] Rendering AuthRoutes (user is not logged in via Redux)');
      return <AuthRoutes />;
    }
  };

  return (
    <BrowserRouter>
      <Toaster position="top-right" />
      
      {/* FCM Message Flow Debugger - Enable for development */}
      {/* <FCMDebugger 
        enabled={process.env.NODE_ENV === 'development'} 
        position="bottom-right" 
      /> */}
      
      {/* Temporary debug overlay - helps diagnose white screen issue */}
      {/* {showDebugOverlay && (
        <div style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          backgroundColor: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '10px 10px 8px 10px',
          borderRadius: '4px',
          fontSize: '11px',
          zIndex: 9999,
          maxWidth: '260px',
          fontFamily: 'monospace',
          boxShadow: '0 2px 8px rgba(0,0,0,0.4)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <strong>App Debug</strong>
            <button
              type="button"
              onClick={() => setShowDebugOverlay(false)}
              style={{
                marginLeft: '8px',
                border: 'none',
                background: 'transparent',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '12px',
                lineHeight: 1,
                padding: '0 4px'
              }}
              aria-label="Close debug info"
            >
              ✕
            </button>
          </div>
          <div>Redux Login: {auth?.isUserLogin ? '✓' : '✗'}</div>
          <div>Redux User: {auth?.user ? '✓' : '✗'}</div>
          <div>Redux Email: {auth?.user?.email || 'none'}</div>
          <div>Storage: {sessionStorage.getItem("user") ? '✓' : '✗'}</div>
        </div>
      )} */}
      {renderContent()}
    </BrowserRouter>
  );
};

export default App;
